import { Injectable, signal } from '@angular/core';
import { Location } from '@angular/common';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TwoFactorGateReason } from '../../enums/two-factor-gate-reason.enum';
import { User } from '../../models/entities/user';
import { UserSettingsService } from '../members/user-settings.service';

/**
 * Asks the two-factor enrollment gate for callers with no template of their own: `SuperUser2faGuard`
 * on the way into the app, `FinancialConnectionsService` before KYC, the launchpad card and the v2
 * Treasury shell. `app-two-factor-gate-dialog-host` at the app root renders the dialog bound to
 * `open`.
 *
 * `ask()` resolves `true` only once the user has enrolled; every other way out resolves `false`.
 * `askUnlessEnrolled()` skips the dialog for a user who already is.
 * Kept free of component imports: the guard injects it, and the dialog imports the guard's caller.
 */
@Injectable({ providedIn: 'root' })
export class TwoFactorGateDialogStateService {
    readonly open = signal(false);
    readonly reason = signal<TwoFactorGateReason | undefined>(undefined);

    private _result$: Subject<boolean> | null = null;

    constructor(location: Location, private _userSettings: UserSettingsService) {
        // Browser back/forward cancels, as MatDialog's closeOnNavigation did (even with disableClose).
        location.subscribe(() => this.answer(false));
    }

    ask(reason?: TwoFactorGateReason): Observable<boolean> {
        this.answer(false);
        this._result$ = new Subject<boolean>();
        this.reason.set(reason);
        this.open.set(true);

        return this._result$.asObservable();
    }

    /**
     * SD-11542: `ask()`, but only for a user who is not enrolled yet. An enrolled user gets `true`
     * without the dialog ever opening; opening it anyway made it flash until the hosted component
     * read the same answer and closed itself.
     *
     * Reads your own user record, the same read the gate's two-factor component makes, because the
     * member record is refused to an un-enrolled super (SD-10091). If the read fails the gate opens
     * as before and does its own check, so a failed read never skips enrollment.
     */
    askUnlessEnrolled(reason?: TwoFactorGateReason): Observable<boolean> {
        return this._userSettings.getOwnUser().pipe(
            map((user: User) => user.isGoogleAuthVerificationEnrolled || user.isSmsAuthVerificationEnrolled),
            catchError(() => of(false)),
            switchMap((enrolled) => enrolled ? of(true) : this.ask(reason)),
        );
    }

    answer(enrolled: boolean): void {
        const result$ = this._result$;
        this._result$ = null;
        this.open.set(false);
        result$?.next(enrolled);
        result$?.complete();
    }
}
